import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  CollectionCapability,
  CollectionRole,
  hasCollectionCapability,
} from '@qkindex/contracts';
import { CheckIcon, RemoveIcon } from '../../../components/ui/icons';

const roles: CollectionRole[] = ['viewer', 'editor', 'administrator', 'owner'];
const permissions: Array<{
  label: string;
  capability?: CollectionCapability;
  ownerOnly?: boolean;
  ai?: boolean;
}> = [
  { label: 'View inventory' },
  { label: 'Create, edit, import & export items', capability: 'editInventory' },
  { label: 'Move items to bin & restore', capability: 'editInventory' },
  { label: 'Use AI', ai: true },
  { label: 'Rename collection', capability: 'renameCollection' },
  { label: 'Manage tags & sharing', capability: 'administerInventory' },
  { label: 'Permanently delete items', capability: 'administerInventory' },
  { label: 'Move items to another collection', capability: 'moveItemsOut' },
  {
    label: 'Manage editors, viewers & their invitations',
    capability: 'manageMembers',
  },
  { label: 'View activity', capability: 'viewActivity' },
  {
    label: 'Manage administrators & their invitations',
    capability: 'manageAdministrators',
  },
  { label: 'Set GPS location', ownerOnly: true },
  { label: 'Manage billing & AI policy', capability: 'manageBilling' },
  { label: 'Transfer ownership', capability: 'manageOwnership' },
  { label: 'Delete collection', capability: 'manageOwnership' },
];

export function CollectionRolesSection() {
  return (
    <>
      <Table
        aria-label="Collection role permissions"
        size="small"
        sx={{
          tableLayout: 'fixed',
          '& th, & td': {
            px: { xs: 0.25, sm: 0.5 },
            py: 1.5,
            overflowWrap: 'anywhere',
          },
          '& th:first-of-type': { width: '40%' },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>Permission</TableCell>
            {roles.map((role) => (
              <TableCell
                key={role}
                align="center"
                sx={{ fontSize: 12, color: 'primary.main' }}
                aria-label={
                  role === 'administrator' ? 'Administrator' : undefined
                }
              >
                {role === 'administrator'
                  ? 'Admin'
                  : role.charAt(0).toUpperCase() + role.slice(1)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {permissions.map((permission) => (
            <TableRow key={permission.label}>
              <TableCell
                component="th"
                scope="row"
                sx={{ fontSize: 13, fontWeight: 400 }}
              >
                {permission.label}
              </TableCell>
              {roles.map((role) => {
                const conditional =
                  permission.ai &&
                  (role === 'editor' || role === 'administrator');
                const allowed = permission.ai
                  ? role !== 'viewer'
                  : permission.ownerOnly
                    ? role === 'owner'
                    : permission.capability
                      ? hasCollectionCapability(role, permission.capability)
                      : true;
                return (
                  <TableCell
                    key={role}
                    align="center"
                    aria-label={
                      conditional
                        ? 'With owner permission'
                        : allowed
                          ? 'Allowed'
                          : 'Not allowed'
                    }
                  >
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        color: allowed ? 'primary.main' : 'text.disabled',
                        opacity: allowed ? 1 : 0.5,
                      }}
                    >
                      {allowed ? (
                        <CheckIcon aria-hidden="true" />
                      ) : (
                        <RemoveIcon aria-hidden="true" />
                      )}
                      {conditional && <span aria-hidden="true">*</span>}
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 2 }}
      >
        * Requires the owner’s permission. See the toggle in the Collection's
        General Section at bottom.
      </Typography>
    </>
  );
}
